/**
 * useCreateAppJob Hook
 *
 * Custom React Query mutation hook for creating appJobs.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { appJobService } from '../../../api/app-job.service';
import type { AppJobCreateDTO } from '../../../types/app-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface CreateAppJobInput {
  applicationId: string;
  data: AppJobCreateDTO;
}

interface UseCreateAppJobOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for creating a appJob
 * @param tenantId - Tenant ID
 * @param options - Success/error callbacks
 */
export const useCreateAppJob = (
  tenantId: string,
  options?: UseCreateAppJobOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, data }: CreateAppJobInput) =>
      appJobService.createAppJob(tenantId, applicationId, data),
    onSuccess: () => {
      // Invalidate all appJob queries for this tenant
      queryClient.invalidateQueries({
        queryKey: ['appJobs', tenantId],
        exact: false,
      });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
